import * as React from 'react'
import { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { LoadContentDocument, ProcessContentDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import ContentBuilder from 'modules/trainings/components/content/Builder'
import { contentRegistry } from 'modules/trainings/components/content/ContentRegistry'
import { ContentItem } from 'modules/trainings/lib/content/ContentItem'
import { graphqlContentMapper } from 'modules/trainings/lib/content/mappers'
import { FeatureWall } from 'modules/core/components/FeatureWall'

const ContentBuilderPage = observer(() => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { id: trainingId } = useParams()
  const session = useCurrentSession()

  const { data, loading } = useQuery(LoadContentDocument, {
    variables: {
      trainingId: Number(trainingId),
    },
  })

  const [processContent, { loading: isSaving }] = useMutation(ProcessContentDocument, {
    extractorFn(mutationData) {
      return mutationData.trainingsMutations.processContentTrainingsTraining
    },
  })

  const collection = useMemo(() => {
    const modules = data?.trainings.contentModules
    if (loading || !modules) {
      return null
    }
    return graphqlContentMapper.fromGraphQL(modules, Number(trainingId))
  }, [trainingId, data, loading])

  if (!collection || loading) {
    return <Loading expand />
  }

  return (
    <FeatureWall featureName="dev_training_lms">
      <ResponsivePage.Body
        variant="fullscreen"
        onActionClick={(action: string) => {
          if (action === 'save') {
            if (!isSaving) {
              // Use GraphQL mapper to convert from domain to GraphQL input
              const contentModules = graphqlContentMapper.toGraphQL(collection)

              processContent({
                variables: {
                  authorId: session.employee.id,
                  companyId: session.company.id,
                  trainingId: collection.trainingId,
                  contentModules,
                },
              }).then((result) => {
                const handled = handleResult(result, () => {
                  app.setFlash({
                    type: 'wrong',
                    message: i18n.t('trainings.content.save.error'),
                  })
                })

                if (handled.success) {
                  app.setFlash({
                    type: 'correct',
                    message: i18n.t('trainings.content.save.success'),
                  })
                }
              })
            }
          } else if (action === 'back') {
            return navigate(
              navigation.trainings.list.details_revamp.view.overview.fullPath({
                id: Number(trainingId),
              })
            )
          }
        }}
      >
        <ContentBuilder
          tocItems={collection.modules.map((contentModule) => ({
            id: contentModule.uuid,
            label: contentModule.title,
            onClick: () => collection.setSelected(contentModule.uuid),
            children: contentModule.blocks.map((block) => ({
              id: block.uuid,
              label: block.title,
              onClick: () => collection.setSelected(block.uuid),
            })),
          }))}
          selectedItem={collection.selectedItem}
          trainingId={collection.trainingId}
          allowedContentTypes={
            collection.empty() ? ['module'] : contentRegistry.getAll().map((def) => def.typeString)
          }
          onReorder={(orderedStructure) => collection.reorder(orderedStructure)}
          onAddContent={({ contentItem }: { contentItem: ContentItem }) => {
            collection.addItem(contentItem)
          }}
          onEditContent={({ uuid, contentItem }: { uuid: string; contentItem: ContentItem }) => {
            collection.editItem(uuid, contentItem)
          }}
          onDeleteContent={(uuid: string) => {
            collection.deleteItem(uuid)
          }}
        />
      </ResponsivePage.Body>
    </FeatureWall>
  )
})

export default ContentBuilderPage
